import React, { useContext, useEffect, useState } from 'react';

import {
  ProductPageTemplate,
  ContentGeneratorDrawer,
  PageGeneratorThemeProvider,
  useProductDefaultValue,
  BrandPageTemplate,
} from 'components/page-generators';
import DrawerContent from 'components/JsonApi/components/DrawerContent';
import {
  AccordionNameEnum,
  BundleJsonApiStates,
  BundlesEnum,
  BundleStates,
  FieldTypeEnum,
  FormSubmitEnum,
  parseDataToPreview,
  parseFetchedEntity,
  useEntities,
} from 'utils/drupalEntities';
import { useFieldArray, useForm } from 'react-hook-form';
import DropZoneErrorDialog from 'components/JsonApi/components/dialogs/DropZoneErrorDialog';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { LoaderContext } from 'context/LoaderContextProvider';
import { backHomeLink } from 'utils/constants';
import SaveOptionsDialog from 'components/JsonApi/components/dialogs/SaveOptionsDialog';
import InfoDialog from 'components/JsonApi/components/dialogs/InfoDialog';
import { useAuth } from 'services/auth';

const NodeEdit = ({ jsonApiLang, bundle }) => {
  if (!jsonApiLang || !bundle) return <></>;
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const { getCurrentLoggedUserParams } = useAuth();
  const {
    listEntityFieldsForm,
    getEntity,
    handleHeaderBtnClick,
    handleOpenDrawerClick,
    onEditData,
    setCustomErrors,
    setPreviewData,
    setOpen,
    previewData,
    generalAccordionComponents,
    basicAccordionComponents,
    additionalContents,
    brandName,
    btnCliked,
    submittedData,
    customErrors,
    open,
  } = useEntities();
  const defaultValue = useProductDefaultValue();
  const methods = useForm({ defaultValues: {} });
  const { getValues, setValue, control, watch, reset } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalContents',
  });
  const value = { ...defaultValue };
  const { setLoaderActive } = useContext(LoaderContext);
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  // States
  const [errorDialog, setErrorDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState({
    isOpen: false,
    i18n: i18n.info.preview,
    img: '',
  });
  const [saveOptionsDialog, setSaveOptionsDialog] = useState({
    isOpen: false,
    i18n: i18n.info.save,
    options: [
      { value: '0', label: i18n.info.save.options.save },
      { value: '1', label: i18n.info.save.options.doNotSave },
    ],
  });
  const [fetchedNode, setFetchedNode] = useState();
  const [productLangCode, setProductLangCode] = useState();
  const [isNotDraft, setIsNotDraft] = useState(true);
  const [isExhibitorGold, setIsExhibitorGold] = useState(false);

  // Constants
  const isProduct = bundle === BundlesEnum.PRODUCT;

  // Effects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langcode = params.get('langcode')?.toLowerCase();
    bundle &&
      listEntityFieldsForm({
        locale: jsonApiLang,
        bundle,
        productLangCode: langcode || jsonApiLang,
      });
  }, [bundle]);

  useEffect(() => {
    const fetchNode = async () => {
      // Se è scheda espositore, chiedi info isGold dell'utente loggato
      showLoader();
      if (!isProduct) {
        const userParams = await getCurrentLoggedUserParams();
        const isGold = userParams?.roles?.includes('exhibitor_gold') || false;
        setIsExhibitorGold(isGold);
      }
      const editNodeId = window.location.pathname.split('/').pop();
      const params = new URLSearchParams(location.search);
      const langcode = params.get('langcode')?.toLowerCase();
      setProductLangCode(langcode || jsonApiLang);
      const nodeResponse = await getEntity({
        useLoader: false,
        langCode: langcode || jsonApiLang,
        bundleId: editNodeId,
        bundle: bundle,
        // Handle Dynamic
        include: isProduct
          ? 'field_cover, field_immagine_anteprima, field_classe_merceologica, field_sotto_categoria, field_categoria, field_private_collezione, field_content, field_content.field_image, field_content.field_immagine, field_content.field_slider_detail.bp_image_field, field_content.field_sheet_image, field_content.field_designer, field_content.field_materiali, field_content.field_caratteristiche, field_content.field_immagine_libera_full'
          : 'menu_link, field_contents, field_contents.field_slider_detail.bp_image_field, field_cover, field_logo, field_stanze, field_contents.field_immagine_vs',
      });
      setIsNotDraft(
        nodeResponse?.data?.attributes?.moderation_state ===
          BundleJsonApiStates[bundle][BundleStates.REVIEW]
      );
      const allComponents = [
        ...generalAccordionComponents,
        ...basicAccordionComponents,
        {
          name: 'title',
          field_type: FieldTypeEnum.STRING,
          isRequired: true,
          label: isProduct
            ? i18n.errors.requiredElements.productName
            : i18n.errors.requiredElements.brandName,
        },
      ];
      const { entity: parsedData, additionalEntity } = await parseFetchedEntity(
        {
          entityResponse: nodeResponse,
          components: allComponents,
        }
      );
      if (parsedData) {
        const parsedPreviewData = parseDataToPreview({
          ...parsedData,
          ...additionalEntity,
        });
        setPreviewData({
          ...parsedPreviewData,
          id: editNodeId,
        });
        setFetchedNode({
          ...parsedData,
          ...additionalEntity,
          id: editNodeId,
        });
        reset({ ...parsedData, ...additionalEntity });
        setOpen(true);
        hideLoader();
      }
    };
    if (
      generalAccordionComponents?.length &&
      basicAccordionComponents?.length &&
      location?.pathname
    ) {
      fetchNode();
    }
  }, [generalAccordionComponents, basicAccordionComponents, location.pathname]);

  useEffect(() => {
    if (btnCliked && submittedData) {
      if (btnCliked === FormSubmitEnum.BACK) {
        if (isNotDraft) {
          showLoader();
          window.location.href = `${backHomeLink}/${jsonApiLang}/admin/sdm/content`;
        } else
          setSaveOptionsDialog({
            i18n: i18n.info.save,
            options: [
              { value: '0', label: i18n.info.save.options.save },
              { value: '1', label: i18n.info.save.options.doNotSave },
            ],
            isOpen: true,
          });
      } else if (btnCliked === FormSubmitEnum.TRANSLATE) {
        setSaveOptionsDialog({
          i18n: i18n.info.translate,
          options: [
            { value: '0', label: i18n.info.translate.options.save },
            { value: '1', label: i18n.info.translate.options.doNotSave },
          ],
          isOpen: true,
        });
      } else if (btnCliked === FormSubmitEnum.PUBLISH) {
        setErrorDialog({
          isOpen: true,
          i18n: i18n.info.beforeApprovation,
          requiredMembers: [],
        });
      } else {
        onEditData(
          btnCliked,
          fetchedNode,
          reset,
          productLangCode,
          jsonApiLang,
          isProduct ? BundlesEnum.PRODUCT : BundlesEnum.BRAND,
          btnCliked === FormSubmitEnum.PUBLISH
            ? `${backHomeLink}/${jsonApiLang}/admin/sdm/content`
            : ''
        );
      }
    }
  }, [btnCliked, submittedData]);

  useEffect(() => {
    if (customErrors) {
      setErrorDialog({ isOpen: true, ...customErrors });
    }
  }, [customErrors]);

  // Functions
  const handleBackOptionSelected = () => {
    showLoader();
    const selectedOption = getValues('save') || '1';
    const redirectLink =
      btnCliked === FormSubmitEnum.BACK
        ? `${backHomeLink}/${jsonApiLang}/admin/sdm/content`
        : `${backHomeLink}/${jsonApiLang}/sdm-editor/node/${
            isProduct ? BundlesEnum.PRODUCT : BundlesEnum.BRAND
          }/${fetchedNode.id}?langcode=${
            productLangCode === 'it' ? 'EN' : 'IT'
          }`;
    if (selectedOption === '0')
      onEditData(
        btnCliked,
        fetchedNode,
        reset,
        productLangCode,
        jsonApiLang,
        isProduct ? BundlesEnum.PRODUCT : BundlesEnum.BRAND,
        redirectLink
      );
    else window.location.href = redirectLink;
  };

  const handleSubmitLogic = () =>
    btnCliked &&
    btnCliked === FormSubmitEnum.PUBLISH &&
    onEditData(
      btnCliked,
      fetchedNode,
      reset,
      productLangCode,
      jsonApiLang,
      isProduct ? BundlesEnum.PRODUCT : BundlesEnum.BRAND,
      `${backHomeLink}/${jsonApiLang}/admin/sdm/content`
    );

  // Renders
  const renderErrorDialog = () => {
    if (!errorDialog.isOpen) return null;
    return (
      <DropZoneErrorDialog
        onClose={() => {
          setErrorDialog({ isOpen: false, text: '' });
        }}
        i18n={{
          ...errorDialog.i18n,
          agree: 'Ok',
        }}
        requiredMembers={errorDialog?.requiredMembers || []}
        onAccept={handleSubmitLogic}
      />
    );
  };
  const renderInfoDialog = () => {
    if (!infoDialog.isOpen) return null;
    return (
      <InfoDialog
        onClose={() => {
          setInfoDialog({
            i18n: { title: '' },
            isOpen: false,
            img: '',
          });
        }}
        i18n={{
          ...infoDialog.i18n,
          agree: 'Ok',
        }}
        img={infoDialog.img}
      />
    );
  };

  const renderSaveOptionsDialog = () => {
    if (!saveOptionsDialog.isOpen) return null;
    return (
      <SaveOptionsDialog
        onClose={() => {
          setSaveOptionsDialog((prev) => ({ ...prev, isOpen: false }));
        }}
        onAgree={() => handleBackOptionSelected()}
        i18n={{
          ...saveOptionsDialog.i18n,
        }}
        options={saveOptionsDialog?.options}
        control={control}
      />
    );
  };

  return (
    <PageGeneratorThemeProvider>
      {previewData?.id && (
        <>
          {isProduct ? (
            <>
              <ProductPageTemplate {...value} {...previewData} {...brandName} />
            </>
          ) : (
            <BrandPageTemplate {...value} {...previewData} {...brandName} />
          )}
        </>
      )}
      <ContentGeneratorDrawer
        open={open}
        onOpen={() => handleOpenDrawerClick(reset)}
        onBtnClicked={(btn) => handleHeaderBtnClick(btn, getValues())}
        hideButtons={isNotDraft}
        productLangCode={productLangCode}
      >
        <DrawerContent
          generalData={generalAccordionComponents}
          basicData={basicAccordionComponents}
          additionalData={additionalContents}
          setCustomErrors={setCustomErrors}
          setValue={setValue}
          getValues={getValues}
          control={control}
          watch={watch}
          fields={fields}
          append={append}
          remove={remove}
          langCode={jsonApiLang}
          isBrand={!isProduct}
          productLangCode={productLangCode}
          openAccordions={[AccordionNameEnum.GENERAL]}
          setInfoDialog={setInfoDialog}
          isExhibitorGold={isExhibitorGold}
        />
      </ContentGeneratorDrawer>
      {renderErrorDialog()}
      {renderSaveOptionsDialog()}
      {renderInfoDialog()}
    </PageGeneratorThemeProvider>
  );
};

export default NodeEdit;
